import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  useTheme,
  Stack,
  Container,
} from "@mui/material";
import {
  getAllBanners,
  createBanner,
  editBanner,
  deleteBanner,
} from "../../../context/Post";
import PostList from "./PostList";
import PostForm from "./PostForm";
import ConfirmDialog from "../ConfirmDialog";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

const PostComponent = () => {
  const theme = useTheme();
  const [posts, setPosts] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [editingPost, setEditingPost] = useState(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    const data = await getAllBanners();
    setPosts(data.banners);
  };

  const handleCreatePost = async (postData) => {
    await createBanner(postData);
    fetchPosts();
  };

  const handleEditPost = async (postData) => {
    await editBanner(editingPost._id, postData);
    fetchPosts();
    setEditingPost(null);
  };

  // Post waiting for delete confirmation.
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const closeDeleteDialog = () => {
    setPendingDeleteId(null);
    setDeleteError("");
  };

  const handleDeletePost = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteBanner(pendingDeleteId);
      await fetchPosts();
      closeDeleteDialog();
    } catch (error) {
      setDeleteError(error.message || "Could not delete this post.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        py: 4,
        px: { xs: 2, sm: 3 },
        backgroundColor: theme.palette.background.default,
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: "bold",
            color: theme.palette.text.primary,
          }}
        >
          Manage Posts
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setEditingPost({})}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            px: 2.5,
            py: 1,
            fontWeight: "medium",
          }}
        >
          Create New Post
        </Button>
      </Stack>

        <Divider sx={{ mb: 3 }} />
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          p: { xs: 3, md: 5 },
          borderRadius: 2,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(180deg, #252018, #1d1915)"
              : "linear-gradient(180deg, #ffffff, #fafafa)",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 4px 15px rgba(0,0,0,0.5)"
              : "0 4px 15px rgba(0,0,0,0.1)",
        }}
      >

        {/* Post Form */}
        {editingPost && (
          <PostForm
            post={editingPost}
            onSave={handleCreatePost}
            onEdit={handleEditPost}
            onCancel={() => setEditingPost(null)}
          />
        )}

        {/* Post List */}
        <PostList
          posts={posts}
          filter={activeFilter}
          onEdit={setEditingPost}
          onDelete={setPendingDeleteId}
          setFilter={setActiveFilter}
        />
      </Paper>

      <ConfirmDialog
        open={Boolean(pendingDeleteId)}
        title="Delete this post?"
        message="It will be removed from the app for everyone."
        loading={deleting}
        error={deleteError}
        onConfirm={handleDeletePost}
        onClose={closeDeleteDialog}
      />
    </Container>
  );
};

export default PostComponent;
